import React from 'react'
import { YStack, styled } from 'tamagui'

import { Metadata } from '@newComponents/Metadata'
import { H1, H2, Paragraph, Strong } from '@newComponents/Text'
import { GradientBackgroundScreen } from '@newComponents/Screen'
import { Card } from '@newComponents/Card'
import { Link, ExternalLink } from '@newComponents/Link'

export const TermsOfServiceScreen = () => {
  return (
    <>
      <Metadata title='Terms of Service' />

      <GradientBackgroundScreen alignItems='center'>
        <YStack gap='$8'>
          <Section>
            <H1>Terms of Service</H1>

            <Paragraph size='$4' color='$grey7'>
              Last updated: February 24, 2026
            </Paragraph>

            <Paragraph>
              Welcome to Guild, operated by Guild Community Host Inc. ("Guild",
              "we", "us", or "our"). By accessing or using our platform at
              guild.host, including our website, mobile applications, and
              related services (collectively, the "Service"), you agree to be
              bound by these Terms of Service ("Terms").
            </Paragraph>

            <Paragraph>
              Please read these Terms carefully. If you do not agree to these
              Terms, you may not access or use the Service.
            </Paragraph>
          </Section>

          <Section>
            <H2>1. Eligibility</H2>

            <Paragraph>
              You must be at least 16 years of age to use the Service. By using
              the Service, you represent and warrant that you meet this age
              requirement. If you are under the age of majority in your
              jurisdiction, you must have permission from a parent or legal
              guardian to use the Service.
            </Paragraph>
          </Section>

          <Section>
            <H2>2. Account Registration</H2>

            <Paragraph>
              To access certain features of the Service, you must create an
              account by providing a valid email address. You may sign in using
              a magic link sent to your email or by setting a password.
            </Paragraph>

            <Paragraph>
              You are responsible for maintaining the confidentiality of your
              account credentials and for all activity that occurs under your
              account. You agree to notify us immediately of any unauthorized
              use of your account.
            </Paragraph>

            <Paragraph>
              You may add multiple email addresses to your account, each of
              which must be verified. You must maintain at least one verified
              email address on your account at all times.
            </Paragraph>
          </Section>

          <Section>
            <H2>3. Description of Service</H2>

            <Paragraph>
              Guild is a community management platform that enables users to:
            </Paragraph>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                {'\u2022'} Create and manage community groups ("Guilds") and
                networks of Guilds
              </Paragraph>
              <Paragraph>
                {'\u2022'} Organize and attend Events, including virtual and
                in-person gatherings
              </Paragraph>
              <Paragraph>
                {'\u2022'} Create and share Presentations linked to Events
              </Paragraph>
              <Paragraph>
                {'\u2022'} Purchase tickets for Events and make donations to
                Guilds
              </Paragraph>
              <Paragraph>
                {'\u2022'} Follow other users and Guilds to stay updated on
                community activity
              </Paragraph>
              <Paragraph>
                {'\u2022'} Post updates and engage with community content
              </Paragraph>
              <Paragraph>
                {'\u2022'} Embed Guild content on external websites
              </Paragraph>
            </YStack>
          </Section>

          <Section>
            <H2>4. User Content</H2>

            <Paragraph>
              You retain ownership of any content you submit, post, or display
              through the Service ("User Content"), including text, images,
              photos, and presentation materials.
            </Paragraph>

            <Paragraph>
              By submitting User Content, you grant Guild a worldwide,
              non-exclusive, royalty-free license to use, reproduce, modify,
              display, and distribute your User Content solely for the purpose
              of operating and providing the Service. This includes displaying
              your content to other users, generating social media previews, and
              rendering embeddable content on third-party websites.
            </Paragraph>

            <Paragraph>
              You represent and warrant that you have all necessary rights to
              submit your User Content and that it does not violate any
              third-party rights or applicable laws.
            </Paragraph>
          </Section>

          <Section>
            <H2>5. Acceptable Use</H2>

            <Paragraph>You agree not to use the Service to:</Paragraph>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                {'\u2022'} Violate any applicable laws or regulations
              </Paragraph>
              <Paragraph>
                {'\u2022'} Infringe on the intellectual property or privacy
                rights of others
              </Paragraph>
              <Paragraph>
                {'\u2022'} Post or transmit content that is unlawful, harmful,
                threatening, abusive, harassing, defamatory, or otherwise
                objectionable
              </Paragraph>
              <Paragraph>
                {'\u2022'} Impersonate any person or entity, or falsely state or
                misrepresent your affiliation with a person or entity
              </Paragraph>
              <Paragraph>
                {'\u2022'} Interfere with or disrupt the Service, servers, or
                networks connected to the Service
              </Paragraph>
              <Paragraph>
                {'\u2022'} Attempt to gain unauthorized access to any part of
                the Service or other accounts
              </Paragraph>
              <Paragraph>
                {'\u2022'} Use the Service for spam, phishing, or distributing
                malware
              </Paragraph>
              <Paragraph>
                {'\u2022'} Scrape, crawl, or otherwise collect data from the
                Service without our written permission
              </Paragraph>
            </YStack>

            <Paragraph>
              We reserve the right to remove any content or suspend any account
              that we determine, in our sole discretion, violates these Terms.
            </Paragraph>
          </Section>

          <Section>
            <H2>6. Payments and Transactions</H2>

            <Paragraph>
              Guild facilitates payment transactions between Event organizers
              and attendees through our integration with Stripe. When you
              purchase tickets or make donations through the Service:
            </Paragraph>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                {'\u2022'} Payment processing is handled by Stripe, Inc. Your
                payment information is collected and processed directly by
                Stripe and is subject to{' '}
                <ExternalLink href='https://stripe.com/legal'>
                  Stripe's Terms of Service
                </ExternalLink>{' '}
                and{' '}
                <ExternalLink href='https://stripe.com/privacy'>
                  Privacy Policy
                </ExternalLink>
              </Paragraph>
              <Paragraph>
                {'\u2022'} Guild does not store your credit card or payment card
                details
              </Paragraph>
              <Paragraph>
                {'\u2022'} Ticket prices, applicable fees, and refund policies
                are set by Event organizers. Guild may charge a platform fee as
                disclosed at the time of purchase
              </Paragraph>
              <Paragraph>
                {'\u2022'} Donations to Guilds are processed via Stripe Connect
                and are subject to the minimum donation amount set by the Guild
              </Paragraph>
            </YStack>

            <Paragraph>
              Event organizers who accept payments through the Service must
              maintain a valid Stripe Connect account and comply with Stripe's
              terms.
            </Paragraph>
          </Section>

          <Section>
            <H2>7. Event Attendance and Data Sharing</H2>

            <Paragraph>
              When you attend an Event or join a Guild, you may be asked to
              consent to share certain information (such as your email address)
              with the Event organizer or Guild administrators. Your consent to
              share this information is requested upon registration.
            </Paragraph>

            <Paragraph>
              Event organizers may export attendee data for Events they manage.
              Guild organizers may similarly export member data. By consenting
              to share your information, you acknowledge that the organizer may
              use it to communicate with you about the Event or Guild
              activities.
            </Paragraph>
          </Section>

          <Section>
            <H2>8. Third-Party Integrations</H2>

            <Paragraph>
              The Service integrates with third-party platforms, including but
              not limited to:
            </Paragraph>

            <YStack paddingLeft='$4' gap='$2'>
              <Paragraph>
                <Strong>Bluesky (AT Protocol):</Strong> Guilds may optionally
                connect to Bluesky to automatically post Event announcements.
                This integration is configured by Guild organizers and requires
                their explicit opt-in.
              </Paragraph>
              <Paragraph>
                <Strong>Meetup:</Strong> Guilds may sync Events from Meetup.
              </Paragraph>
              <Paragraph>
                <Strong>Google Maps:</Strong> Venue locations may be displayed
                using Google Maps, subject to{' '}
                <ExternalLink href='https://policies.google.com/terms'>
                  Google's Terms of Service
                </ExternalLink>
                .
              </Paragraph>
            </YStack>

            <Paragraph>
              Your use of third-party integrations is subject to those third
              parties' own terms and privacy policies.
            </Paragraph>
          </Section>

          <Section>
            <H2>9. Intellectual Property</H2>

            <Paragraph>
              The Service, including but not limited to its design, features,
              code, and branding, is owned by Guild Community Host Inc. and is
              protected by intellectual property laws. You may not copy, modify,
              distribute, sell, or lease any part of the Service without our
              written permission.
            </Paragraph>

            <Paragraph>
              "Guild" and the Guild logo are trademarks of Guild Community Host
              Inc. You may not use these trademarks without our prior written
              consent.
            </Paragraph>
          </Section>

          <Section>
            <H2>10. Disclaimers</H2>

            <Paragraph>
              The Service is provided on an "as is" and "as available" basis
              without warranties of any kind, either express or implied,
              including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, and
              non-infringement.
            </Paragraph>

            <Paragraph>
              Guild does not warrant that the Service will be uninterrupted,
              error-free, or free of harmful components. We do not endorse or
              assume responsibility for any User Content, Events, or third-party
              services accessible through the Service.
            </Paragraph>
          </Section>

          <Section>
            <H2>11. Limitation of Liability</H2>

            <Paragraph>
              To the maximum extent permitted by applicable law, Guild Community
              Host Inc. and its officers, directors, employees, and agents shall
              not be liable for any indirect, incidental, special,
              consequential, or punitive damages, including without limitation
              loss of profits, data, or goodwill, arising out of or in
              connection with your use of the Service.
            </Paragraph>

            <Paragraph>
              Guild is not responsible for the actions, content, or policies of
              Event organizers, Guild administrators, or other users. Any
              disputes between users should be resolved directly between the
              parties involved.
            </Paragraph>
          </Section>

          <Section>
            <H2>12. Account Termination</H2>

            <Paragraph>
              You may request deletion of your account at any time by contacting
              us at{' '}
              <ExternalLink href='mailto:hi@guild.host'>
                hi@guild.host
              </ExternalLink>
              . Deleting your account will permanently remove your profile,
              memberships, content, and associated data.
            </Paragraph>

            <Paragraph>
              We may suspend or terminate your account if you violate these
              Terms or if we reasonably believe your conduct is harmful to the
              Service, other users, or third parties.
            </Paragraph>
          </Section>

          <Section>
            <H2>13. Changes to These Terms</H2>

            <Paragraph>
              We may update these Terms from time to time. If we make material
              changes, we will notify you by email or through the Service. Your
              continued use of the Service after any changes constitutes your
              acceptance of the updated Terms.
            </Paragraph>
          </Section>

          <Section>
            <H2>14. Governing Law</H2>

            <Paragraph>
              These Terms shall be governed by and construed in accordance with
              the laws of the State of Delaware, United States, without regard
              to its conflict of law principles. Any disputes arising from these
              Terms or your use of the Service shall be subject to the exclusive
              jurisdiction of the state and federal courts located in the State
              of Delaware.
            </Paragraph>
          </Section>

          <Section>
            <H2>15. Contact</H2>

            <Paragraph>
              If you have any questions about these Terms, please contact us at{' '}
              <ExternalLink href='mailto:hi@guild.host'>
                hi@guild.host
              </ExternalLink>
              .
            </Paragraph>

            <Paragraph>
              See also our <Link href='/legal/privacy'>Privacy Policy</Link>.
            </Paragraph>
          </Section>
        </YStack>
      </GradientBackgroundScreen>
    </>
  )
}

export default TermsOfServiceScreen

const Section = styled(Card, {
  tag: 'section',
  gap: '$6',
  maxWidth: 600,
  width: '100%',
})
